import React from "react";
import {
  Box,
  Button,
  Command,
  CommandGroup,
  CommandGroupLabel,
  CommandInput,
  DrawerPinned,
  CommandItem,
  CommandList,
  CommandSeparator,
  Icon,
  Kbd,
  KbdGroup,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type IconName,
} from "@salestrak/ui";

import {
  CustomizeNavigationDialog,
  type CustomizeNavigationTab,
} from "#app/components/navigation/module-rail/customize-navigation-dialog";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { CREATE_PROJECT_SHORTCUT } from "#app/lib/hooks/use-create-project-hotkey";

type CreateRow = {
  id: string;
  icon: IconName;
  label: string;
  /** Secondary line under the label — the "what is this" teaching text. */
  description?: string;
  /** Visible but inert — a coming-soon surface holding its slot in the menu. */
  disabled?: boolean;
  /** Keycaps trailing the label, for a row that also has a global hotkey. */
  shortcut?: string[];
  onSelect?: () => void;
};

/** One menu section: an optional group label over its rows. Separators render
 *  BETWEEN non-empty sections, never as part of one. */
type CreateSection = {
  label?: string;
  rows: CreateRow[];
};

type CreateMenuPopoverProps = {
  /** Accessible name for the "+" trigger, e.g. `"Home actions"`. */
  ariaLabel: string;
  /** Present = the viewer may create projects (create.items + an editable
   * workflow — the caller owns the gate, exactly as the old menu did). */
  onCreateProject?: (() => void) | undefined;
  /** Present = the viewer may create workflows (create.workflows). */
  onCreateWorkflow?: (() => void) | undefined;
  /** Present = dashboards are on for this workspace (the widgets flag — the
   * Dashboards module menu's own gate). Absent, the row holds its slot
   * disabled so the menu's shape is stable across workspaces. */
  onCreateDashboard?: (() => void) | undefined;
  /** Which tab the pinned Customize Sidebar row opens the dialog on. */
  customizeInitialTab?: CustomizeNavigationTab;
};

/**
 * The rich create popover on the Home and Workflows module-menu headers: a
 * searchable, sectioned catalog of everything creatable from here. Teams
 * keeps the plain options menu — its actions (team, invite) are its own.
 *
 * Grammar: a 320px Popover hosting the inline Command (autocomplete) — the
 * same input + keyboard-nav machinery as global search, content-sized. The
 * search filters rows by label; a section whose rows all filter out drops
 * with its separator. The leading "Create" section is the entity set, whose
 * one row is present only while its caller-owned gate passes. Dashboard
 * holds its slot disabled where the caller passes no action.
 */
export function CreateMenuPopover({
  ariaLabel,
  onCreateProject,
  onCreateWorkflow,
  onCreateDashboard,
  customizeInitialTab = "navigation",
}: CreateMenuPopoverProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [customizeOpen, setCustomizeOpen] = React.useState(false);

  // Reset the filter per open, not on close — clearing on close would make
  // the rows visibly reshuffle under the popover's exit transition.
  const onOpenChange = React.useCallback((isOpen: boolean) => {
    if (isOpen) setQuery("");
    setOpen(isOpen);
  }, []);

  // Sections in menu order. Separators render BETWEEN non-empty sections, so
  // a fully filtered-out section never leaves a stray double rule behind.
  const sections = React.useMemo<CreateSection[]>(() => {
    const pick = (action: () => void) => () => {
      setOpen(false);
      action();
    };
    const entityRows: CreateRow[] = [];
    if (onCreateProject != null) {
      entityRows.push({
        id: "project",
        icon: ENTITY_KIND_ICON.issue,
        label: "Project",
        shortcut: CREATE_PROJECT_SHORTCUT,
        onSelect: pick(onCreateProject),
      });
    }
    return [
      { label: "Create", rows: entityRows },
      {
        rows:
          onCreateWorkflow != null
            ? [
                {
                  id: "workflow",
                  icon: ENTITY_KIND_ICON.workflow,
                  label: "Workflow",
                  description: "Track Projects through stages and views",
                  onSelect: pick(onCreateWorkflow),
                },
              ]
            : [],
      },
      {
        rows: [
          {
            id: "dashboard",
            icon: "layout-dashboard",
            label: "Dashboard",
            disabled: onCreateDashboard == null,
            ...(onCreateDashboard != null && {
              onSelect: pick(onCreateDashboard),
            }),
          },
        ],
      },
      {
        rows: [
          {
            id: "customize-sidebar",
            icon: "settings-2",
            label: "Customize Sidebar",
            onSelect: pick(() => setCustomizeOpen(true)),
          },
        ],
      },
    ];
  }, [
    onCreateProject,
    onCreateWorkflow,
    onCreateDashboard,
  ]);

  const q = query.trim().toLowerCase();
  const visibleSections = sections
    .map((section) => ({
      ...section,
      rows: section.rows.filter((row) =>
        row.label.toLowerCase().includes(q),
      ),
    }))
    .filter((section) => section.rows.length > 0);

  return (
    <>
      {/* `responsive`: a bottom sheet on a phone, where this opens from
        * inside the nav sheet and its search takes the keyboard. */}
      <Popover onOpenChange={onOpenChange} open={open} responsive>
        {/* The shadow rides a pill wrapper matching the Button — same
          * construction (and reasons) as the Teams menu trigger. */}
        <Box direction="row" rounded="full" shadow="control">
          <Tooltip>
            <TooltipTrigger
              render={
                <PopoverTrigger
                  render={
                    <Button
                      aria-label={ariaLabel}
                      size="sm"
                      variant="outline"
                      // Light: no border — the `control` shadow carries the edge.
                      className="not-dark:border-transparent!"
                    />
                  }
                >
                  <Icon name="plus" size="sm" className="opacity-100" />
                  <Icon color="muted" name="chevron-down" size="sm" />
                </PopoverTrigger>
              }
            />
            <TooltipPopup side="top">Create</TooltipPopup>
          </Tooltip>
        </Box>
        {/* Drops from the trigger's LEADING edge and opens rightward into
          * the page: the create control sits at the pane header's trailing
          * end, so an `end`-aligned popup unfolds back across the sidebar it
          * was opened from and covers the nav it is not about. */}
        <PopoverPopup
          align="start"
          className="w-80"
          padding="none"
          sheetTitle="Create"
          side="bottom"
        >
          <Command mode="none" onValueChange={setQuery} value={query}>
            {/* The selector-popover search grammar (option picker et al.): a
              * bordered input floating inset, not a flush ⌘K header row. In
              * the phone sheet it pins above the scroller with the caption. */}
            <DrawerPinned>
              <CommandInput
                inset
                placeholder="Describe what to create..."
                startAddon={null}
              />
            </DrawerPinned>
            <CommandList>
              {visibleSections.length === 0 ? (
                <Box align="center" paddingY="md">
                  <Text color="muted" size="sm">
                    No matches
                  </Text>
                </Box>
              ) : (
                visibleSections.map(({ label, rows }, sectionIndex) => (
                  <React.Fragment key={rows[0]?.id ?? sectionIndex}>
                    {sectionIndex > 0 && <CommandSeparator bleed />}
                    <CommandGroup>
                      {label != null && (
                        <CommandGroupLabel>{label}</CommandGroupLabel>
                      )}
                      {rows.map((row) => (
                        <CommandItem
                          key={row.id}
                          disabled={row.disabled}
                          onClick={row.onSelect}
                          value={row.id}
                        >
                          <Box
                            align={row.description != null ? "start" : "center"}
                            direction="row"
                            fill="width"
                            gap="sm"
                          >
                            <Icon
                              color="muted"
                              name={row.icon}
                              size="sm"
                              // Optically centers the glyph on the first line
                              // of a two-line row.
                              className={
                                row.description != null ? "mt-0.5" : undefined
                              }
                            />
                            <Box
                              direction="column"
                              flex={1}
                              minWidth={0}
                              shrink
                            >
                              <Text size="sm" truncate>
                                {row.label}
                              </Text>
                              {row.description != null && (
                                <Text color="muted" size="xs">
                                  {row.description}
                                </Text>
                              )}
                            </Box>
                            {row.shortcut != null && (
                              <KbdGroup>
                                {row.shortcut.map((key) => (
                                  <Kbd key={key}>{key}</Kbd>
                                ))}
                              </KbdGroup>
                            )}
                          </Box>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </React.Fragment>
                ))
              )}
            </CommandList>
          </Command>
        </PopoverPopup>
      </Popover>
      <CustomizeNavigationDialog
        initialTab={customizeInitialTab}
        onOpenChange={setCustomizeOpen}
        open={customizeOpen}
      />
    </>
  );
}
